import { HttpClient, httpResource } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  missionDebriefSchema,
  missionTargetSchema,
  pendingEliminationSchema,
  playerMissionSchema,
  type MissionDebrief,
  type MissionTarget,
  type PendingElimination,
  type PlayerMission,
} from '@killer-party/contracts';
import { firstValueFrom, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { field } from './http-parse';
import { readResource, ReadResource } from './read-resource';
import { PlayerSessionService } from './player-session.service';

type MissionId = () => number | undefined;

// Routes joueur /api/missions/:missionId (jeton ajoute par agentTokenInterceptor).
// Les lectures sont des httpResource, a creer dans un contexte d'injection.
@Injectable({ providedIn: 'root' })
export class MissionService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(PlayerSessionService);
  private readonly baseUrl = `${environment.apiBaseUrl}/api/missions`;

  mission(missionId: MissionId): ReadResource<PlayerMission> {
    return readResource(
      httpResource(() => this.url(missionId), { parse: field('mission', playerMissionSchema) }),
    );
  }

  // null : plus de cible (joueur sorti du jeu, ou vainqueur).
  target(missionId: MissionId): ReadResource<MissionTarget | null> {
    return readResource(
      httpResource(() => this.url(missionId, 'target'), {
        parse: field('target', missionTargetSchema.nullable()),
      }),
    );
  }

  // Elimination declaree contre le joueur, a confirmer ou contester (TD-015).
  pendingElimination(missionId: MissionId): ReadResource<PendingElimination | null> {
    return readResource(
      httpResource(() => this.url(missionId, 'elimination'), {
        parse: field('elimination', pendingEliminationSchema.nullable()),
      }),
    );
  }

  debrief(missionId: MissionId): ReadResource<MissionDebrief> {
    return readResource(
      httpResource(() => this.url(missionId, 'debrief'), {
        parse: field('debrief', missionDebriefSchema),
      }),
    );
  }

  getMission(missionId: number): Promise<PlayerMission> {
    return firstValueFrom(
      this.http
        .get<unknown>(`${this.baseUrl}/${missionId}`)
        .pipe(map(field('mission', playerMissionSchema))),
    );
  }

  // Depart du lobby (TD-012) : le jeton est revoque, la session n'a plus d'usage.
  async leave(missionId: number): Promise<void> {
    await this.post(missionId, 'leave');
    this.session.remove(missionId);
  }

  eliminate(missionId: number, targetId: number): Promise<void> {
    return this.post(missionId, `targets/${targetId}/eliminate`);
  }

  // Retire une declaration faite par erreur, tant qu'elle n'est pas validee.
  cancelElimination(missionId: number, targetId: number): Promise<void> {
    return this.post(missionId, `targets/${targetId}/cancel-elimination`);
  }

  // Nouvelle action tiree au hasard, au plus une fois toutes les 5 minutes (TD-031).
  changeAction(missionId: number, targetId: number): Promise<void> {
    return this.post(missionId, `targets/${targetId}/change-action`);
  }

  confirmElimination(missionId: number): Promise<void> {
    return this.post(missionId, 'elimination/confirm');
  }

  contestElimination(missionId: number): Promise<void> {
    return this.post(missionId, 'elimination/contest');
  }

  abandon(missionId: number): Promise<void> {
    return this.post(missionId, 'abandon');
  }

  private url(missionId: MissionId, path?: string): string | undefined {
    const id = missionId();
    return id === undefined ? undefined : [this.baseUrl, id, path].filter(Boolean).join('/');
  }

  private async post(missionId: number, path: string): Promise<void> {
    await firstValueFrom(this.http.post(`${this.baseUrl}/${missionId}/${path}`, null));
  }
}
